/**
 * Canonical 5-coach taxonomy — the backend source of truth (matches the
 * RAG system's 97 knowledge nodes + `api/index.js:1125-1128` coach
 * prompt branching + `tokens.css` --c-* color tokens).
 *
 * Claude Design's Phase 2 handoff proposed a different 5-coach set
 * (dropped gear-advisor, added Strength Coach, recolored mindset).
 * That proposal was REJECTED — see
 * `.claude/design-artifacts/2026-04-21-onboarding-landing/Decisions & Handoff.md`
 * decision #1. The AI-letter naming pattern (Airo / Gair / Vaira / Onai /
 * Kairu) IS adopted, mapped to our real coaches below. The names are spelled
 * in proper-noun case (Part 6 ruling, 2026-10-12); `renderCoachName` finds the
 * "ai" particle case-insensitively to highlight it.
 *
 * Phase 1 dashboard copy uses ROLE ("From your Training Coach") — that
 * stays unchanged. NAMES surface in Phase 2 Welcome (Step 7) only.
 * Phase 4 chat will pull names through to the chat header.
 */

export type CoachId =
  | 'training-coach'
  | 'gear-advisor'
  | 'race-day-strategist'
  | 'recovery-nutrition'
  | 'mental-skills-coach';

export type Coach = {
  /** Backend ID used for RAG routing + API calls. Never change. */
  id: CoachId;
  /** Display name — the "AI-letter" voice introduced in Phase 2. */
  name: string;
  /** Plain-English role — what the coach does. */
  role: string;
  /** CSS custom property for the coach's identity color. */
  token: string;
  /** Hex value of `token` — duplicated for places that can't read CSS vars (e.g. inline SVG fill). Keep in sync with `tokens.css`. */
  hex: string;
  /** Two-letter fallback (e.g. avatar when the name's first letter collides). */
  initial: string;
  /** Short descriptor — used in Step 7 Welcome coach-card list. */
  desc: string;
  /**
   * Part 7 (onboarding_2_0_v1) — the landing's own role string (strings 44, 47,
   * 50, 53, 56 in `Landing/v2/sections/content.ts`). ADDED, never replacing
   * `role`: the landing previews, `PlanShowcase` and the primitives page still
   * read `role` and `desc` and must render unchanged. The reveal reads this one
   * behind the flag; `coaches.landing.junction.test.ts` holds it equal to
   * the landing's list.
   */
  landingRole: string;
  /** Part 7 — the landing's own description (strings 45, 48, 51, 54, 57). See `landingRole`. */
  landingDesc: string;
};

export const COACHES: Coach[] = [
  {
    id: 'training-coach',
    name: 'Airo',
    role: 'Training Coach',
    token: 'var(--c-training)',
    hex: '#046F6F',
    initial: 'A',
    desc: 'Session structure, zones, and weekly load.',
    landingRole: 'Training coach',
    landingDesc: 'Workouts and weekly load.',
  },
  {
    id: 'gear-advisor',
    name: 'Gair',
    role: 'Gear Advisor',
    token: 'var(--c-gear)',
    hex: '#8B6B1F',
    initial: 'G',
    desc: 'Shoes, kit, wearables: what fits you and when to replace.',
    landingRole: 'Gear advisor',
    landingDesc: 'Shoes and gear, and when to replace them.',
  },
  {
    id: 'race-day-strategist',
    name: 'Vaira',
    role: 'Race Coach',
    token: 'var(--c-race)',
    hex: '#1E40AF',
    initial: 'V',
    desc: 'Pacing, taper strategy, and split targets.',
    landingRole: 'Race coach',
    landingDesc: 'Pacing, tapering and race plans.',
  },
  {
    id: 'recovery-nutrition',
    name: 'Onai',
    role: 'Recovery & Nutrition',
    token: 'var(--c-recovery)',
    hex: '#0F7A5A',
    initial: 'O',
    desc: 'Rest signals, back-off weeks, fueling, injury prevention.',
    landingRole: 'Recovery and nutrition',
    landingDesc: 'Food, sleep and rest days.',
  },
  {
    id: 'mental-skills-coach',
    name: 'Kairu',
    role: 'Mindset Coach',
    token: 'var(--c-mental)',
    hex: '#5E3A9E',
    initial: 'K',
    desc: 'Mental cues, race-day confidence, focus routines.',
    landingRole: 'Mindset coach',
    landingDesc: 'Focus and race-day nerves.',
  },
];

/** Lookup helper for id → Coach. */
export function coachById(id: CoachId): Coach {
  const c = COACHES.find((c) => c.id === id);
  if (!c) throw new Error(`Unknown coach id: ${id}`);
  return c;
}

/** The lead coach — Airo (training) — delivers the Step 7 first message. */
export const LEAD_COACH: Coach = coachById('training-coach');
